import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import trainingService from '../../services/trainingService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const TrainingDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const { page, setPage, totalPages, paginatedItems: paginatedSubmissions } = usePagination(submissions);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await trainingService.listSubmissions();
      if (data.success) {
        setSubmissions(data.submissions);
      } else {
        setError(data.message || 'Failed to load submissions');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load training submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const totalCount = submissions.length;
  const withPdfCount = submissions.filter(s => s.hasPdf).length;
  const pendingCount = submissions.filter(s => !s.hasPdf).length;

  return (
    <>
      <Navbar />
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">
              <i className="fas fa-graduation-cap text-success me-2"></i>
              Training Dashboard
            </h2>
            <p className="text-muted mb-0">Manage training certificates</p>
          </Col>
          <Col xs="auto">
            <Button
              variant="success"
              onClick={() => navigate('/training/new')}
            >
              <i className="fas fa-plus me-2"></i>
              New Training Certificate
            </Button>
          </Col>
        </Row>

        {/* Stats */}
        <Row className="mb-4">
          <Col md={4}>
            <Card className="border-0 shadow-sm text-center">
              <Card.Body>
                <h3 className="text-primary mb-1">{totalCount}</h3>
                <small className="text-muted">Total Submissions</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm text-center">
              <Card.Body>
                <h3 className="text-success mb-1">{withPdfCount}</h3>
                <small className="text-muted">Certificates Generated</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm text-center">
              <Card.Body>
                <h3 className="text-warning mb-1">{pendingCount}</h3>
                <small className="text-muted">Pending PDF Generation</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {/* Submissions Table */}
        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <h5 className="mb-0">All Training Submissions</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="success" />
                <p className="mt-2 text-muted">Loading submissions…</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-folder-open fa-3x text-muted mb-3 d-block"></i>
                <p className="text-muted">No training submissions yet.</p>
                <Button variant="success" onClick={() => navigate('/training/new')}>
                  <i className="fas fa-plus me-2"></i>Create First Certificate
                </Button>
              </div>
            ) : (
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Trainee</th>
                    <th>Course</th>
                    <th>Cert Type</th>
                    <th>Cert No</th>
                    <th>Issue Date</th>
                    <th>Valid Until</th>
                    <th>PDF</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubmissions.map((sub, idx) => (
                    <tr key={sub.id}>
                      <td className="text-muted">{idx + 1}</td>
                      <td>
                        <strong>{sub.traineeName || '—'}</strong>
                      </td>
                      <td>
                        <div>{sub.courseName}</div>
                        <small className="text-muted">{sub.courseCode}</small>
                      </td>
                      <td>
                        <Badge bg={sub.certType === 'ADNOC' ? 'primary' : 'info'}>
                          {sub.certType}
                        </Badge>
                      </td>
                      <td>{sub.certNo || <span className="text-muted">—</span>}</td>
                      <td>{sub.issueDate ? formatDate(sub.issueDate) : '—'}</td>
                      <td>{sub.validUntil ? formatDate(sub.validUntil) : '—'}</td>
                      <td>
                        {sub.hasPdf ? (
                          <Badge bg="success"><i className="fas fa-check me-1"></i>Ready</Badge>
                        ) : (
                          <Badge bg="warning" text="dark">Pending</Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => navigate(`/training/submissions/${sub.id}`)}
                          >
                            <i className="fas fa-eye me-1"></i>View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => navigate(`/training/submissions/${sub.id}/edit`)}
                          >
                            <i className="fas fa-edit me-1"></i>Edit
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <TablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('en-GB');
};

export default TrainingDashboard;
